import React, { useEffect, useState } from "react";

import { formatEuro } from "../wizardCreateHelpers";
import WizardIcon from "./WizardIcon";

// Read-only echo of the wizard's own local state. It is not a storefront
// renderer — it only shows what the merchant has entered so far.
const CreatePreviewPanel = ({
  t,
  step,
  title,
  price,
  compareAtPrice,
  description,
  brandName,
  categoryLabel,
  colors,
  sizes,
  images,
  stockByColor,
  totalStock,
  isReadyToPublish,
  getSwatch,
  children,
}) => {
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => {
    setImageIndex(0);
  }, [images.length, images[0]]); // eslint-disable-line react-hooks/exhaustive-deps

  const heading =
    step === 1 ? t("catalogProductStudio.previewTitleWebshop") : t("catalogProductStudio.previewTitle");
  const copy =
    step === 1
      ? t("catalogProductStudio.previewCopyProduct")
      : step === 4
      ? t("catalogProductStudio.previewCopyReview")
      : t("catalogProductStudio.previewCopyVariants");

  const formattedPrice = formatEuro(price);
  const formattedCompare = formatEuro(compareAtPrice);
  const showCompare =
    formattedCompare && formattedPrice && Number(compareAtPrice) > Number(price);
  const metaLine = [brandName, categoryLabel].filter(Boolean).join(" · ");
  const currentImage = images[imageIndex] || null;

  return (
    <aside className="catalog-wizard-preview" aria-label={heading}>
      <div className="catalog-wizard-preview__card">
        <div className="catalog-wizard-preview__head">
          <h3>{heading}</h3>
          <p>{copy}</p>
        </div>

        <div className="catalog-wizard-preview__media">
          {currentImage ? (
            <img src={currentImage} alt={title || t("catalogProductStudio.previewUntitled")} />
          ) : (
            <span className="catalog-wizard-preview__placeholder" role="img" aria-label={t("catalogProductStudio.previewNoImage")}>
              <WizardIcon name="image" size={48} strokeWidth={1.4} />
            </span>
          )}
          {step === 1 ? (
            <span className="catalog-wizard-preview__badge">{t("catalogProductStudio.previewDraft")}</span>
          ) : null}
          {images.length > 1 ? (
            <>
              <button
                type="button"
                className="catalog-wizard-preview__nav catalog-wizard-preview__nav--prev"
                aria-label={t("catalogProductStudio.previewPrevImage")}
                onClick={() => setImageIndex((index) => (index - 1 + images.length) % images.length)}
              >
                <WizardIcon name="chevronLeft" size={18} />
              </button>
              <button
                type="button"
                className="catalog-wizard-preview__nav catalog-wizard-preview__nav--next"
                aria-label={t("catalogProductStudio.previewNextImage")}
                onClick={() => setImageIndex((index) => (index + 1) % images.length)}
              >
                <WizardIcon name="chevronRight" size={18} />
              </button>
              <div className="catalog-wizard-preview__dots" aria-hidden="true">
                {images.slice(0, 8).map((image, index) => (
                  <span key={`${image}-${index}`} className={index === imageIndex ? "is-active" : ""} />
                ))}
              </div>
            </>
          ) : null}
        </div>

        <div className={`catalog-wizard-preview__name${title?.trim() ? "" : " is-placeholder"}`}>
          {title?.trim() || t("catalogProductStudio.previewUntitled")}
        </div>
        {step > 1 && metaLine ? <div className="catalog-wizard-preview__meta">{metaLine}</div> : null}

        <div className="catalog-wizard-preview__price">
          <strong className={formattedPrice ? "" : "is-placeholder"}>{formattedPrice || "€0.00"}</strong>
          {showCompare ? <s>{formattedCompare}</s> : null}
        </div>

        {colors.length ? (
          <>
            {step > 1 ? (
              <div className="catalog-wizard-preview__label">
                {t("catalogProductStudio.previewColors").replace("{count}", String(colors.length))}
              </div>
            ) : null}
            <div className="catalog-wizard-preview__swatches">
              {colors.map((color) => (
                <span
                  key={color}
                  title={color}
                  className="catalog-wizard-swatch catalog-wizard-swatch--lg"
                  style={{ "--catalog-color-swatch-value": getSwatch(color) }}
                />
              ))}
            </div>
          </>
        ) : null}

        {sizes.length ? (
          <>
            {step > 1 ? (
              <div className="catalog-wizard-preview__label">
                {t("catalogProductStudio.previewSizes").replace("{count}", String(sizes.length))}
              </div>
            ) : null}
            <div className="catalog-wizard-preview__sizes">
              {sizes.map((size) => (
                <span key={size}>{size}</span>
              ))}
            </div>
          </>
        ) : null}

        {step === 4 && description?.trim() ? (
          <p className="catalog-wizard-preview__desc">{description}</p>
        ) : null}

        {step === 1 ? (
          <>
            <button type="button" className="catalog-wizard-preview__cart" disabled>
              <WizardIcon name="cart" size={17} />
              {t("catalogProductStudio.previewAddToCart")}
            </button>
            <div className="catalog-wizard-preview__box">
              <p>{t("catalogProductStudio.previewNote")}</p>
            </div>
          </>
        ) : null}

        {step === 2 ? (
          <div className="catalog-wizard-preview__box">
            <strong>{t("catalogProductStudio.previewNextStepTitle")}</strong>
            <p>{t("catalogProductStudio.previewNextStepCopy")}</p>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="catalog-wizard-preview__box">
            <strong>{t("catalogProductStudio.previewStockSummary")}</strong>
            {stockByColor
              .filter((entry) => entry.color)
              .map((entry) => (
                <div key={entry.color} className="catalog-wizard-preview__stock-row">
                  <span>{entry.color}</span>
                  <span>{t("catalogProductStudio.previewUnits").replace("{count}", String(entry.total))}</span>
                </div>
              ))}
            <div className="catalog-wizard-preview__stock-row is-total">
              <span>{t("catalogProductStudio.previewStockTotal")}</span>
              <span>{t("catalogProductStudio.previewUnits").replace("{count}", String(totalStock))}</span>
            </div>
          </div>
        ) : null}

        {step === 4 ? (
          <div className="catalog-wizard-preview__box">
            <strong>{t("catalogProductStudio.previewPublishStatus")}</strong>
            <div className={`catalog-wizard-preview__status${isReadyToPublish ? "" : " is-blocked"}`}>
              {isReadyToPublish
                ? t("catalogProductStudio.previewReady")
                : t("catalogProductStudio.previewNotReady")}
            </div>
            {isReadyToPublish ? <p>{t("catalogProductStudio.previewReadyCopy")}</p> : null}
          </div>
        ) : null}
      </div>

      {step === 3 ? (
        <div className="catalog-wizard-preview__card catalog-wizard-preview__box">
          <strong>{t("catalogProductStudio.previewNextStepTitle")}</strong>
          <p>{t("catalogProductStudio.previewStockNoteCopy")}</p>
        </div>
      ) : null}

      {children}
    </aside>
  );
};

export default CreatePreviewPanel;
